import { makeChoices, nearMisses } from "../../distractors";
import { signed, text } from "../../format";
import type { QuestionDraft, Rng, TopicDef } from "../../types";
import { both, DIV, MUL, numeric, q } from "../helpers";

function tier1(rng: Rng): QuestionDraft {
  const a = rng.int(-20, 20);
  let b = rng.int(-20, 20);
  if (a >= 0 && b >= 0) b = -rng.int(1, 20);
  if (b === 0) b = -7;
  const op = rng.pick(["+", "-"] as const);
  const result = op === "+" ? a + b : a - b;
  const other = op === "+" ? a - b : a + b;
  const choice4 = makeChoices(rng, result, [other, -result, ...nearMisses(result)], (r) => r.int(-40, 40));
  const expr = `${a} ${op === "+" ? "+" : "−"} ${signed(b)}`;
  const pl =
    op === "-" && b < 0
      ? [
          `Odejmowanie liczby ujemnej to dodawanie: ${a} − (${b}) = ${a} + ${-b}.`,
          `${a} + ${-b} = ${result}.`,
        ]
      : [
          `Na osi liczbowej startujemy w ${a} i przesuwamy się o ${Math.abs(b)} w ${b >= 0 === (op === "+") ? "prawo" : "lewo"}.`,
          `Wynik: ${result}.`,
        ];
  const en =
    op === "-" && b < 0
      ? [`Subtracting a negative is adding: ${a} − (${b}) = ${a} + ${-b}.`, `${a} + ${-b} = ${result}.`]
      : [
          `On the number line start at ${a} and move ${Math.abs(b)} to the ${b >= 0 === (op === "+") ? "right" : "left"}.`,
          `Result: ${result}.`,
        ];
  return q(both(`Oblicz: ${expr} = ?`, `Calculate: ${expr} = ?`), numeric(result), pl, en, { choice4 });
}

function tier2(rng: Rng): QuestionDraft {
  const a = rng.int(2, 12) * (rng.chance(0.5) ? -1 : 1);
  const b = rng.int(2, 12) * (rng.chance(0.5) ? -1 : 1);
  const useDiv = rng.chance(0.4);
  const product = a * b;
  const result = useDiv ? a : product;
  const expr = useDiv ? `${product} {DIV} ${signed(b)}` : `${a} {MUL} ${signed(b)}`;
  const samePl = a < 0 === b < 0;
  const choice4 = makeChoices(
    rng,
    result,
    [-result, Math.abs(result), result + (useDiv ? 1 : Math.abs(b))],
    (r) => r.int(-50, 50),
  );
  const signRulePl = samePl ? "Takie same znaki → wynik dodatni." : "Różne znaki → wynik ujemny.";
  const signRuleEn = samePl ? "Same signs → positive result." : "Different signs → negative result.";
  return q(
    both(
      `Oblicz: ${expr.replace("{DIV}", DIV.pl).replace("{MUL}", MUL.pl)} = ?`,
      `Calculate: ${expr.replace("{DIV}", DIV.en).replace("{MUL}", MUL.en)} = ?`,
    ),
    numeric(result),
    [
      signRulePl,
      `Liczymy bez znaków: ${useDiv ? `${Math.abs(product)} : ${Math.abs(b)} = ${Math.abs(a)}` : `${Math.abs(a)} · ${Math.abs(b)} = ${Math.abs(product)}`}.`,
      `Wynik: ${result}.`,
    ],
    [
      signRuleEn,
      `Ignore the signs first: ${useDiv ? `${Math.abs(product)} ÷ ${Math.abs(b)} = ${Math.abs(a)}` : `${Math.abs(a)} × ${Math.abs(b)} = ${Math.abs(product)}`}.`,
      `Result: ${result}.`,
    ],
    { choice4 },
  );
}

function tier3(rng: Rng): QuestionDraft {
  if (rng.chance(0.35)) {
    // the classic trap: (−a)² versus −a²
    const a = rng.int(2, 9);
    const negFirst = rng.chance(0.5);
    const result = negFirst ? a * a : -(a * a);
    const expr = negFirst ? `(−${a})²` : `−${a}²`;
    const choice4 = makeChoices(
      rng,
      result,
      [-result, negFirst ? -2 * a : 2 * a, negFirst ? 2 * a : -2 * a],
      (r) => r.int(-100, 100),
    );
    return q(
      both(`Oblicz: ${expr} = ?`, `Calculate: ${expr} = ?`),
      numeric(result),
      negFirst
        ? [`Nawias mówi: podnosimy do kwadratu całą liczbę −${a}.`, `(−${a}) · (−${a}) = ${a * a}.`]
        : [`Bez nawiasu potęgowanie jest pierwsze: ${a}² = ${a * a}.`, `Potem minus: −${a * a}.`],
      negFirst
        ? [`The bracket means the whole number −${a} is squared.`, `(−${a}) × (−${a}) = ${a * a}.`]
        : [`Without a bracket the power comes first: ${a}² = ${a * a}.`, `Then the minus: −${a * a}.`],
      { choice4 },
    );
  }
  const a = rng.int(-15, 15);
  const b = rng.int(2, 9) * (rng.chance(0.5) ? -1 : 1);
  const c = rng.int(2, 9) * (rng.chance(0.5) ? -1 : 1);
  const op = rng.pick(["+", "-"] as const);
  const product = b * c;
  const result = op === "+" ? a + product : a - product;
  const leftToRight = (op === "+" ? a + b : a - b) * c;
  const choice4 = makeChoices(
    rng,
    result,
    [leftToRight, -result, op === "+" ? a - product : a + product],
    (r) => r.int(-120, 120),
  );
  const exprPl = `${a} ${op === "+" ? "+" : "−"} ${signed(b)} · ${signed(c)}`;
  const exprEn = `${a} ${op === "+" ? "+" : "−"} ${signed(b)} × ${signed(c)}`;
  return q(
    both(`Oblicz: ${exprPl} = ?`, `Calculate: ${exprEn} = ?`),
    numeric(result),
    [
      `Najpierw mnożenie: ${signed(b)} · ${signed(c)} = ${product}.`,
      `Potem: ${a} ${op === "+" ? "+" : "−"} ${signed(product)} = ${result}.`,
    ],
    [
      `Multiplication first: ${signed(b)} × ${signed(c)} = ${product}.`,
      `Then: ${a} ${op === "+" ? "+" : "−"} ${signed(product)} = ${result}.`,
    ],
    { choice4 },
  );
}

export const integersOps: TopicDef = {
  id: "sp6.integers_ops",
  level: "sp6",
  name: text("Liczby całkowite", "Integers"),
  modes: ["numeric", "choice4"],
  generate: (rng, tier) => (tier === 1 ? tier1(rng) : tier === 2 ? tier2(rng) : tier3(rng)),
};
